import { Cartesian3, Color, HorizontalOrigin, NearFarScalar } from "cesium";
import { customIcons } from "../../../../assets/icons/customIcons/customIcons";

export interface CreateIconForFeatureOptions {
  feature: any;
  position: any;
  viewer: any;
  entityManager?: {
    add: (entity: any) => void;
  };
  cleanupRefs?: {
    iconEntities?: React.MutableRefObject<any[]>;
  };
}

export interface CreatedIconEntities {
  backgroundEntity: any;
  iconEntity: any | null;
}

/**
 * Shared utility function to create Cesium icons for features
 * Works for both drawn features and GIS data
 */
export function createIconForFeature({
  feature,
  position,
  viewer,
  entityManager,
  cleanupRefs,
}: CreateIconForFeatureOptions): CreatedIconEntities | null {
  if (!feature.properties || !feature.properties.icon) return null;

  const currentFeatureId = feature.id || feature.properties?.id;
  const iconCode = feature.properties.icon;

  // Find the icon details from the customIcons array
  const iconDetails = customIcons.find((icon) => icon.key === iconCode);
  if (!iconDetails) return null;

  // Determine background color (like in Mapbox implementation)
  let backgroundColor = Color.fromCssColorString("#fed062"); // Default background color
  if (feature.properties.color) {
    try {
      backgroundColor = Color.fromCssColorString(feature.properties.color);
    } catch (e) {
      // Use default if color parsing fails
    }
  }

  const entityAdder = entityManager || viewer.entities;

  // Create background icon entity first
  const backgroundEntity = entityAdder.add({
    position: position,
    featureId: currentFeatureId + "_icon_bg",
    label: {
      text: "\uE85E", // Same background icon as in Mapbox implementation
      font: "500 24px custom-icons-2", // Match CSS definition: font-weight 200
      pixelOffset: new Cartesian3(0, 0, 0), // Centered
      fillColor: backgroundColor,
      verticalOrigin: 0, // CENTER
      horizontalOrigin: HorizontalOrigin.CENTER,
      //   disableDepthTestDistance: Number.POSITIVE_INFINITY,
      scaleByDistance: new NearFarScalar(1.0e2, 1.0, 1.0e7, 0.5),
    },
  });

  // Mark as decorative to exclude from view calculations
  backgroundEntity.isDecorative = true;
  backgroundEntity.computeBoundingSphere = () => null;

  // Create foreground icon with 1-second delay to match the working pattern
  let iconEntity: any = null;

  try {
    iconEntity = entityAdder.add({
      position: position,
      featureId: currentFeatureId + "_icon",
      label: {
        text: iconCode,
        font: "500 14px custom-icons-2", // Match CSS definition: font-weight 200
        pixelOffset: new Cartesian3(0, -1, 0), // Slight upward adjustment for perfect centering
        fillColor: Color.WHITE,
        verticalOrigin: 0, // CENTER
        horizontalOrigin: HorizontalOrigin.CENTER,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        scaleByDistance: new NearFarScalar(1.0e2, 1.0, 1.0e7, 0.5), // Scale with distance
      },
    });

    // Mark as decorative to exclude from view calculations
    if (iconEntity) {
      iconEntity.isDecorative = true;
      iconEntity.computeBoundingSphere = () => null;

      // Add to cleanup refs if provided
      if (cleanupRefs?.iconEntities) {
        cleanupRefs.iconEntities.current.push(iconEntity);
      }
    }
  } catch (error) {
    console.warn("Failed to create delayed icon entity:", error);
  }

  return { backgroundEntity, iconEntity: null }; // Return null for iconEntity initially due to delay
}
